import type { ReactElement } from 'react';

interface ZoomControlsProps {
  /** 지금 배율(%) — 그림이 아직 없으면 null. */
  readonly percent: number | null;
  readonly isFit: boolean;
  readonly canZoomIn: boolean;
  readonly canZoomOut: boolean;
  /** 확대할 그림이 없다(그리는 중·실패). */
  readonly disabled: boolean;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onFit: () => void;
}

const BUTTON_BASE =
  'inline-flex h-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-input bg-background font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';
const ROUND_BUTTON = `${BUTTON_BASE} size-11 text-lg leading-none`;
const FIT_BUTTON = `${BUTTON_BASE} px-5 text-sm aria-pressed:border-foreground aria-pressed:bg-brand-lime aria-pressed:text-foreground`;

/**
 * 확대 단추 줄(뷰어 설계 V10) — 축소(−)·지금 비율·확대(+)·맞춤. 휴대폰에서도 누르기 쉽게 모두 44px 이상이다.
 * 맞춤 상태면 맞춤 단추가 눌린 모양(라임)이다. 비율은 `<output>`(status)이라 바뀌면 화면낭독기가 읽는다.
 */
export const ZoomControls = ({
  percent,
  isFit,
  canZoomIn,
  canZoomOut,
  disabled,
  onZoomIn,
  onZoomOut,
  onFit,
}: ZoomControlsProps): ReactElement => (
  <div className="flex items-center gap-2 self-end sm:ml-auto sm:self-auto">
    <button
      type="button"
      aria-label="축소"
      disabled={disabled || !canZoomOut}
      onClick={onZoomOut}
      className={ROUND_BUTTON}
    >
      <span aria-hidden>−</span>
    </button>
    <output
      aria-label="확대 비율"
      className="min-w-14 text-center text-sm font-medium tabular-nums"
    >
      {percent === null ? '—' : `${percent}%`}
    </output>
    <button
      type="button"
      aria-label="확대"
      disabled={disabled || !canZoomIn}
      onClick={onZoomIn}
      className={ROUND_BUTTON}
    >
      <span aria-hidden>+</span>
    </button>
    <button
      type="button"
      aria-pressed={isFit}
      disabled={disabled}
      onClick={onFit}
      className={FIT_BUTTON}
    >
      맞춤
    </button>
  </div>
);
